import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { TResetPassword } from "../types";
import { Field, FieldProps, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, TextField, Typography } from "@/components/kit";
import SvgIcon from "@/components/common/SvgIcon";
import { useState } from "react";
import useChangePassword from "@/hooks/query/auth/useChangePassword";
function ChangePassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { t } = useTranslation(["auth", "common"]);
  const { mutate, isPending } = useChangePassword();
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState<boolean>(false);
  const validationSchema = Yup.object({
    password: Yup.string()
      .required(t("common:password_required"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
        t("common:password_strong"),
      ),
    confirmPassword: Yup.string()
      .required(t("common:password_required"))
      .oneOf([Yup.ref("password")], t("common:passwords_must_match")),
  });

  const onSubmit = async (values: TResetPassword) =>
    mutate(
      {
        payload: {
          newPassword: values.password,
          confirmPassword: values.confirmPassword,
          token: token ?? "",
        },
      },
      {
        onSuccess() {
          navigate("/auth/login");
        },
      },
    );

  const formik = useFormik<TResetPassword>({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    onSubmit,
    validationSchema,
  });

  return (
    <FormikProvider value={formik}>
      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-col justify-center h-full"
      >
        <div className={"flex flex-col gap-2 mb-[32px]"}>
          <Typography variant="Title2-Bold">{"Change Password"}</Typography>
          <Typography variant="BodyPlus2-Regular" color="text-gray-700">
            {t("define_new_strong_password")}
          </Typography>
        </div>
        <div className="flex flex-col justify-start items-start gap-3">
          <Field name="password">
            {({ field, meta: { error, touched } }: FieldProps) => (
              <TextField
                {...field}
                error={touched && error}
                label={t("auth:new_pass")}
                type={showPass ? "text" : "password"}
                autoComplete="new-password"
                endAdornment={
                  <div
                    className={"cursor-pointer"}
                    onClick={() => setShowPass((prevState) => !prevState)}
                  >
                    <SvgIcon
                      iconName={
                        (showPass && "eyeOffOutline") || "eyesOnOutline"
                      }
                    />
                  </div>
                }
              />
            )}
          </Field>
          <Field name="confirmPassword">
            {({ field, meta: { error, touched } }: FieldProps) => (
              <TextField
                {...field}
                error={touched && error}
                label={t("confirm_password")}
                type={showPass ? "text" : "password"}
                autoComplete="new-password"
                endAdornment={
                  <div
                    className={"cursor-pointer"}
                    onClick={() => setShowPass((prevState) => !prevState)}
                  >
                    <SvgIcon
                      iconName={
                        (showPass && "eyeOffOutline") || "eyesOnOutline"
                      }
                    />
                  </div>
                }
              />
            )}
          </Field>
        </div>
        <Button
          isLoading={isPending}
          size="lg"
          className={"mt-[32px] w-full"}
          type={"submit"}
        >
          {t("common:confirm")}
        </Button>
      </form>
    </FormikProvider>
  );
}

export default ChangePassword;
